import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
    ActionIcon,
    Box,
    CopyButton,
    Flex,
    Grid,
    Image,
    MediaQuery,
    Paper,
    Stack,
    Text,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
// import { useMediaQuery } from "@mantine/hooks";
import { IconCalendar, IconClipboardText, IconCopy, IconUsers } from "@tabler/icons-react";
import { StarsRating } from "@testuj.to/react";
import dayjs from "dayjs";
import { CampaignState } from "src/graphql/generated/graphql";
import { CampaignQueryDataType } from "src/hooks/campaigns";

import { CampaignBadge } from "../../../CampaignBadge";
import { Link } from "../../../Link";

export interface OverviewWidgetProps {
    campaign: CampaignQueryDataType;
}

export const OverviewWidget = ({ campaign }: OverviewWidgetProps) => {
    const { t } = useTranslation(["common"]);
    const theme = useMantineTheme();
    const [searchParams] = useSearchParams();

    const currentProductId = searchParams.get("productId");

    const productResults = useMemo(() => {
        return (
            campaign?.results?.products?.find((item) => item.productId === currentProductId) ||
            campaign?.results?.products?.[0]
        );
    }, [campaign?.results?.products, currentProductId]);

    const selectedProduct = useMemo(() => {
        return campaign?.products?.find((item) => item.id === currentProductId) || campaign?.products?.[0];
    }, [campaign?.products, currentProductId]);

    // const queryLargerThanSm = useMediaQuery(`(min-width: ${theme.breakpoints.md})`, false);
    const campaignStateStyles = {
        all: "gray",
        draft: "gray",
        opened: "blue",
        inprogress: "cyan",
        closed: "green",
    };

    const mainImage = currentProductId ? selectedProduct?.thumbnailImage?.src : campaign?.thumbnailImage?.src;

    const { showPreviewLink, campaignUrl, previewUrl } = useMemo(() => {
        const showPreviewLink =
            campaign?.state === CampaignState.Draft || campaign?.state === CampaignState.Publishable;
        let campaignPath = `/campaigns/${campaign?.slug}`;

        const domainName = campaign?.mainTenant?.domainName;
        if (campaign?.isHidden) {
            campaignPath = `/campaigns/${campaign?.id}`;
        }

        const previewUrl = `https://${domainName}/login?userType=client&redirect=${encodeURIComponent(campaignPath)}`;

        return {
            showPreviewLink,
            previewUrl,
            campaignUrl: `https://${domainName}${campaignPath}`,
        };
    }, [campaign?.state, campaign?.slug, campaign?.isHidden, campaign?.mainTenant?.domainName, campaign?.id]);

    const campaignEndDate = useMemo(() => {
        const finishedMilestone = campaign?.milestones
            ?.sort((a, b) => a.timestamp - b.timestamp)
            .reverse()
            .find((milestone) => milestone.type === "state:finished");

        if (finishedMilestone) {
            return dayjs(finishedMilestone.timestamp);
        }

        if (campaign?.settings?.closeAt) {
            return dayjs(campaign?.settings?.closeAt).add(campaign?.settings?.submissionPeriodDays, "days");
        }

        return null;
    }, [campaign?.settings?.closeAt, campaign?.settings?.submissionPeriodDays, campaign?.milestones]);

    return (
        <Paper style={{ overflow: "hidden" }} display="flex" radius={12}>
            <Grid style={{ flex: 1 }}>
                <Grid.Col p={0} offset={0} sm={12} md={12} xl="content">
                    {mainImage && (
                        <Image
                            height={280}
                            fit="cover"
                            src={mainImage}
                            style={{
                                [`@media (maxWidth: ${theme.breakpoints.lg}px)`]: {
                                    width: 400,
                                },
                            }}
                        />
                    )}
                </Grid.Col>
                <Grid.Col p={0} offset={0} sm="auto" md={8} xl="auto">
                    <Stack
                        style={{ height: "100%" }}
                        px="clamp(24px, 4vw, 48px)"
                        py="clamp(18px, 4vw, 36px)"
                        spacing={12}
                    >
                        <Text size={24} weight={700}>
                            {currentProductId ? selectedProduct?.name : campaign?.name}
                        </Text>
                        <CampaignBadge color={campaignStateStyles[campaign?.state]} style={{ width: "fit-content" }}>
                            {t(`campaign_state_${campaign?.state}`)}
                        </CampaignBadge>
                        <Box>
                            <Text weight={500}>{t("campaign_previewlink")}</Text>
                            <Flex align="center">
                                <CopyButton value={showPreviewLink ? previewUrl : campaignUrl}>
                                    {({ copied, copy }) => (
                                        <Tooltip label={copied ? t("copied") : t("copy")} withArrow>
                                            <ActionIcon
                                                mr="xs"
                                                size="sm"
                                                variant={copied ? undefined : "subtle"}
                                                color={copied ? "green" : "orange"}
                                                onClick={copy}
                                            >
                                                <IconCopy size="1em" />
                                            </ActionIcon>
                                        </Tooltip>
                                    )}
                                </CopyButton>
                                <Link
                                    href={showPreviewLink ? previewUrl : campaignUrl}
                                    target="_blank"
                                    style={{
                                        display: "block",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis",
                                        maxWidth: "320px",
                                    }}
                                >
                                    {showPreviewLink ? previewUrl : campaignUrl}
                                </Link>
                            </Flex>
                        </Box>
                        {campaign?.isHidden && !showPreviewLink && (
                            <Box>
                                <Text weight={500}>{t("campaign_hiddenlink")}</Text>
                                <Flex align="center">
                                    <CopyButton value={campaignUrl}>
                                        {({ copied, copy }) => (
                                            <Tooltip label={copied ? t("copied") : t("copy")} withArrow>
                                                <ActionIcon
                                                    mr="xs"
                                                    size="sm"
                                                    variant={copied ? undefined : "subtle"}
                                                    color={copied ? "green" : "orange"}
                                                    onClick={copy}
                                                >
                                                    <IconCopy size="1em" />
                                                </ActionIcon>
                                            </Tooltip>
                                        )}
                                    </CopyButton>
                                    <Link
                                        href={campaignUrl}
                                        target="_blank"
                                        style={{
                                            // width: "100%",
                                            display: "block",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            maxWidth: "320px",
                                            // direction: "rtl",
                                        }}
                                    >
                                        {campaignUrl}
                                    </Link>
                                </Flex>
                            </Box>
                        )}
                        <Flex mt="auto" gap={20} wrap="wrap">
                            <Flex gap={8} align="center">
                                <IconCalendar stroke={1.5} size={20} />
                                <Text style={{ whiteSpace: "nowrap" }} size={14}>
                                    {campaign?.settings?.openAt
                                        ? dayjs(campaign?.settings?.openAt).format("DD. MM. YYYY")
                                        : "--"}{" "}
                                    - {campaignEndDate ? campaignEndDate.format("DD. MM. YYYY") : "--"}
                                </Text>
                            </Flex>
                            <Flex gap={8} align="center">
                                <IconClipboardText stroke={1.5} size={20} />
                                <Text style={{ whiteSpace: "nowrap" }} size={14}>
                                    {t("campaign_signups", { count: campaign?.totalApplicationsCount })}
                                </Text>
                            </Flex>
                            <Flex gap={8} align="center">
                                <IconUsers stroke={1.5} size={20} />
                                <Text style={{ whiteSpace: "nowrap" }} size={14}>
                                    {t("campaign_testers_count", { count: campaign?.settings.capacity })}
                                </Text>
                            </Flex>
                        </Flex>
                    </Stack>
                </Grid.Col>
                <Grid.Col
                    display="flex"
                    style={{ justifyContent: "center" }}
                    p={0}
                    offset={0}
                    sm="content"
                    md={4}
                    xl="content"
                >
                    <MediaQuery styles={{ display: "none" }} largerThan="md" smallerThan="xl">
                        <Flex
                            style={{
                                height: "70%",
                                width: 1,
                                backgroundColor: "#DADEE2",
                                marginBlock: "auto",
                            }}
                        />
                    </MediaQuery>
                    <Stack
                        py={32}
                        px="clamp(36px, 5vw, 72px)"
                        spacing={8}
                        align="center"
                        justify="center"
                        style={{ height: "100%" }}
                    >
                        <Text size={60} weight="bold">
                            {(productResults?.totalRating / 2).toFixed(1) || "--"}
                        </Text>
                        <StarsRating rating={productResults?.totalRating / 2 || 0} readOnly />
                        <Text
                            component={Link}
                            size={14}
                            weight={600}
                            color="#30A9D4"
                            underline
                            href={`/campaign/${campaign?.id}/reviews`}
                        >
                            {t("campaign_reviews_count", { count: productResults?.approvedCount || 0 })}
                        </Text>
                    </Stack>
                </Grid.Col>
            </Grid>
        </Paper>
    );
};
